import FullPageLayout from "../../../../templates/FullPageLayout";
import { Button, Card, Col, Form, Row } from "antd";
import { useEffect, useState } from "react";
import { NeedHelp } from "../../../../atoms/CustomIcons";
import { useForm } from "react-hook-form";
import {
  InputField,
  InputNumberField,
  SelectField,
} from "../../../../atoms/FormFields";
import { SelectFieldTags } from "../../../../atoms/FormFields/SelectFieldTags";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../config/hooks";
import {
  getCrewDetails,
  getCrewFormData,
  removeCrewDetailData,
} from "../../ducks/actions";
import { updateCrewById } from "../../ducks/services";
import { SuccessMessage } from "../../../../atoms/Toasts/SuccessMessage";
import { AxiosError } from "axios";
import { ErrorMessage } from "../../../../atoms/Toasts/ErrorMessage";

interface FormValues {
  name: string;
  service_line_id: string[];
  category_ids: string[];
  crew_members: string[];
  crew_leader: string;
  crew_size: number;
}

export default function EditCrews() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const crewId = pathSegments[pathSegments.length - 2];
  const crewFormData = useAppSelector(
    (state) => state?.crewsSubs?.crewFormData
  );
  const crewDetailData = useAppSelector(
    (state) => state?.crewsSubs?.crewDetailData
  );
  const [leaderInOther, setLeaderInOther] = useState<{
    label: string;
    value: string;
    crews?: string[];
  } | null>(null);
  const [memberInOther, setMemberInOther] = useState<
    | {
        label: string;
        value: string;
        crews?: string[];
      }[]
    | null
  >(null);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>();
  const crewName = watch("name");
  const onSubmit = (val: FormValues) => {
    setLoading(true);
    const payload = {
      name: val?.name?.trim(),
      crew_size: val?.crew_size ?? 0,
      crew_leader: val?.crew_leader ?? "",
      crew_members: val?.crew_members ?? [],
      service_line_id: val?.service_line_id ?? [],
      category_ids: val?.category_ids ?? [],
      company_id: companyId,
    };

    updateCrewById(crewId, payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Crew updated");
        navigate(`/crews/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (companyId) {
      dispatch(getCrewFormData(companyId));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    dispatch(removeCrewDetailData());
  }, [dispatch]);

  useEffect(() => {
    if (crewId) {
      dispatch(getCrewDetails(crewId));
    }
  }, [dispatch, crewId]);

  useEffect(() => {
    if (crewDetailData?.data) {
      setValue("name", crewDetailData?.data?.name);

      setValue("category_ids", crewDetailData?.data?.category_ids);
      setValue("crew_leader", crewDetailData?.data?.crew_leader);
      setValue("crew_size", crewDetailData?.data?.crew_size ?? 1);

      if (crewDetailData?.data?.crewServiceLines?.length > 0) {
        setValue(
          "service_line_id",
          crewDetailData?.data?.crewServiceLines?.map(
            (e: { service_line_id: string }) => e?.service_line_id
          )
        );
      }
      if (crewDetailData?.data?.crewMembers?.length > 0) {
        setValue(
          "crew_members",
          crewDetailData?.data?.crewMembers?.map((e: { id: string }) => e?.id)
        );
      }
    }
  }, [crewDetailData?.data, setValue]);

  const crewLeaderChange = (val: string | number | readonly string[]) => {
    const leaderObject = crewFormData?.employees?.find(
      (emp: { value: string; crews?: string[] }) =>
        val === emp?.value && emp?.crews && emp?.crews?.length > 0
    );
    setLeaderInOther(leaderObject ?? null);
  };

  const crewMemberChange = (val: string | number | readonly string[]) => {
    const memberObject = crewFormData?.employees?.filter(
      (emp: { value: string; crews?: string[] }) =>
        Array.isArray(val) &&
        val.some(
          (v) => v === emp?.value && emp?.crews && emp?.crews?.length > 0
        )
    );
    setMemberInOther(memberObject ?? null);
  };
  console.log("crewName:", crewName);
  return (
    <FullPageLayout loading={loading || crewDetailData?.loader}>
      <Button className="btn-sm fixedTopBtn">
        <NeedHelp /> Need help?
      </Button>

      <Row gutter={8} style={{ width: "672px" }}>
        <Col span={24}>
          <div
            className="title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h2> Edit crew </h2>
              <p>Crews are assigned to routes and complete scheduled visits.</p>
            </div>
          </div>
        </Col>

        <Col span={24}>
          <div>
            <Form
              className="clientsForm"
              layout="vertical"
              onFinish={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            >
              <Card>
                <Row gutter={8}>
                  <Col span={24}>
                    <h4 style={{ marginBottom: "16px" }}> Crew details </h4>
                  </Col>
                  <Col span={24}>
                    <SelectFieldTags
                      fieldName="service_line_id"
                      label="Service lines"
                      control={control}
                      selectOption={crewFormData?.service_line}
                      iProps={{
                        placeholder: "Select service lines",
                        mode: "multiple",
                      }}
                      isRequired
                      rules={{
                        required: "Service lines is required.",
                      }}
                      validate={errors.service_line_id && "error"}
                      validMessage={
                        errors.service_line_id && errors.service_line_id.message
                      }
                      convertColorToTags={true}
                    />
                  </Col>
                  <Col span={12} className="count_input">
                    <InputField
                      fieldName="name"
                      label="Crew name"
                      control={control}
                      defaultValue=""
                      iProps={{
                        placeholder: "Enter name",
                        showCount: true,
                        maxLength: 8,
                      }}
                      isRequired
                      rules={{
                        required: "Crew name is required.",
                      }}
                      validate={errors.name && "error"}
                      validMessage={errors.name && errors.name.message}
                    />
                  </Col>
                  <Col span={12}>
                    <InputNumberField
                      fieldName="crew_size"
                      label="Regular crew size"
                      control={control}
                      defaultValue={1}
                      iProps={{
                        placeholder: "Regular crew size",
                        min: 1,
                      }}
                      isRequired
                      rules={{
                        required: "Crew size is required.",
                      }}
                      validate={errors.crew_size && "error"}
                      validMessage={
                        errors.crew_size && errors.crew_size.message
                      }
                    />
                  </Col>
                  <Col span={24}>
                    <SelectField
                      fieldName="crew_leader"
                      label="Crew leader"
                      control={control}
                      selectOption={crewFormData?.employees?.filter(
                        (e: { position_id: string }) => e?.position_id === "2"
                      )}
                      iProps={{
                        placeholder: "Select Crew leader",
                      }}
                      isRequired
                      rules={{
                        required: "Crew leader is required.",
                      }}
                      validate={errors.crew_leader && "error"}
                      validMessage={
                        errors.crew_leader && errors.crew_leader.message
                      }
                      onSelectChange={crewLeaderChange}
                    />
                    {leaderInOther?.crews &&
                      leaderInOther?.crews?.length > 0 && (
                        <div className="warning_crew_text">
                          {`${
                            leaderInOther?.label
                          } also leads crews ${leaderInOther?.crews?.join(
                            ", "
                          )}.`}
                        </div>
                      )}
                  </Col>
                  <Col span={24}>
                    <SelectField
                      fieldName="crew_members"
                      label="Regular crew members"
                      control={control}
                      selectOption={crewFormData?.employees?.filter(
                        (e: { position_id: string }) => e?.position_id === "3"
                      )}
                      iProps={{
                        placeholder: "Select Regular crew members",
                        mode: "multiple",
                      }}
                      onSelectChange={crewMemberChange}
                    />
                    {memberInOther && memberInOther?.length > 0 && (
                      <ul
                        className="warning_crew_text"
                        style={{ paddingLeft: "15px" }}
                      >
                        {memberInOther
                          ?.filter((e) => e.crews?.includes(crewName) === false)
                          ?.map((item: { label: string; crews?: string[] }) => (
                            <li>
                              {`${
                                item?.label
                              } is also assigned to crews ${item?.crews?.join(
                                ", "
                              )}.`}
                            </li>
                          ))}
                      </ul>
                    )}
                  </Col>
                  <Col span={24}>
                    <SelectFieldTags
                      fieldName="category_ids"
                      label="Categories"
                      control={control}
                      selectOption={crewFormData?.category}
                      className="bottom_0"
                      iProps={{
                        placeholder: "Select categories",
                        mode: "multiple",
                      }}
                    />
                  </Col>
                </Row>
              </Card>

              <div className="fixed_btm_card">
                <Card>
                  <div className="centered">
                    <Button
                      htmlType="button"
                      className="outlined-btn"
                      onClick={() => navigate(`/crews/${companyId}`)}
                    >
                      Cancel
                    </Button>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className="mint-btn"
                    >
                      Save & close
                    </Button>
                  </div>
                </Card>
              </div>
            </Form>
          </div>
        </Col>
      </Row>
    </FullPageLayout>
  );
}
